// AnimatedCounter — Phase 19 polish.
//
// Counts a number up from 0 to `value` over `duration` ms when the
// component scrolls into view. Uses Framer Motion's useInView for
// trigger detection so it shares the same threshold the rest of
// Motion.jsx uses.
//
// Usage:
//   <AnimatedCounter value={25} suffix="+" />
//   <AnimatedCounter value={300} suffix=" models" duration={1800} />
//
// Decoupled from data fetching so animation never blocks render.

import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

export default function AnimatedCounter({
  value = 0,
  suffix = "",
  prefix = "",
  duration = 1400,
  className = "",
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (typeof value !== "number" || value <= 0) {
      setDisplay(value);
      return;
    }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // Ease-out cubic so the count slows toward the end
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.floor(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {typeof display === "number" ? display.toLocaleString() : display}
      {suffix}
    </span>
  );
}
